import { CreateResourceParams, queryKeys } from "./ReactQuery/reactQueryService";
import { apiPatch, apiPost } from "./Api/apiCall";
import { QueryClient, UseMutationResult } from "@tanstack/react-query";
import { ProductUnion } from "../types/ProductUnion";
import { BackofficeUser, Roles } from "../types/BackofficeUser";
import { userHasOneOfTheseRoles } from "./backofficeUserService";
import { AiChatAgent, AiChatThread } from "../types/AIChat/AiChatThread";
import { AiChatVoiceTranscription } from "../types/AIChat/AiChatVoiceTranscription";
import { AiChatMessage } from "../types/AIChat/AiChatMessage";

export const transcribeAiChatVoiceRecording = (audio: Blob): Promise<AiChatVoiceTranscription> => {
  const formData = new FormData();
  formData.append("audio", audio, "voice-recording.webm");

  return apiPost("ai_chat/voice_transcriptions", formData);
};

type SendAiChatPromptParams = {
  product: ProductUnion;
  activeThreadId: number | null;
  setActiveThreadId: (threadId: number) => void;
  createMutation: UseMutationResult<any, unknown, CreateResourceParams>;
  queryClient: QueryClient;
  connect: Function;
  reset: Function;
  prompt: string;
  setIsLoading: Function;
  agentType: AiChatAgent;
  activeExecutionStepId?: number | null;
  editedMessageUuid?: string | null;
};

export const sendAiChatPrompt = async ({
  product,
  activeThreadId,
  setActiveThreadId,
  createMutation,
  queryClient,
  connect,
  reset,
  prompt,
  setIsLoading,
  agentType,
  activeExecutionStepId = null,
  editedMessageUuid = null,
}: SendAiChatPromptParams) => {
  setIsLoading(true);

  try {
    let currentThreadId = activeThreadId;
    if (!activeThreadId) {
      const thread: AiChatThread = await createMutation.mutateAsync({
        uri: "ai_chat_threads",
        data: {
          backofficeCase: product.backofficeCase["@id"],
          agent: agentType,
          title: prompt,
        },
      });
      currentThreadId = thread.id;
      setActiveThreadId(currentThreadId);
      await queryClient.invalidateQueries({
        queryKey: queryKeys.collection("ai_chat_threads", { backofficeCase: product.backofficeCase["@id"] }),
      });
    }

    const streamId = connect();

    const messagesUri = "ai_chat_messages";

    await apiPost(messagesUri, {
      content: prompt,
      aiChatThread: "/ai_chat_threads/" + currentThreadId,
      streamId,
      ...(activeExecutionStepId !== null && { activeExecutionStepId, mode: "execution_step_edit" }),
      ...(editedMessageUuid !== null && { editedMessageUuid }),
    });

    await queryClient.invalidateQueries({
      queryKey: queryKeys.collection(messagesUri, { aiChatThread: currentThreadId }),
    });
    await queryClient.invalidateQueries({
      queryKey: queryKeys.item("ai_chat_threads", currentThreadId as number),
    });
  } catch (error) {
    reset();
    setIsLoading(false);
    throw error;
  }
};

export const selectAiChatBranch = async (
  threadId: number,
  currentLeafMessageUuid: string,
): Promise<void> => {
  await apiPatch("ai_chat_threads", threadId, { currentLeafMessageUuid });
};

// Returns the conversation as currently displayed: walks from the thread's selected leaf
// up the parent links to the root and returns that path in top-to-bottom order. Messages
// on other branches are left out; an unknown leaf or a broken chain yields an empty array.
export const getAiChatThreadPath = (
  messages: AiChatMessage[],
  currentLeafMessageUuid: string | null,
): AiChatMessage[] => {
  const messagesByUuid = new Map(messages.map((message) => [message.uuid, message]));
  const path: AiChatMessage[] = [];
  let currentMessage = currentLeafMessageUuid === null ? null : messagesByUuid.get(currentLeafMessageUuid) ?? null;

  while (currentMessage !== null) {
    path.unshift(currentMessage);

    if (currentMessage.parentMessageUuid === null) {
      return path;
    }
    currentMessage = messagesByUuid.get(currentMessage.parentMessageUuid) ?? null;
  }

  return [];
};

// Returns all variants of a message, i.e. its siblings in the message tree (same parent,
// including the message itself). These are the branches the arrow buttons switch between.
export const getAiChatMessageVariants = (
  messages: AiChatMessage[],
  message: AiChatMessage,
): AiChatMessage[] => messages.filter((candidate) => candidate.parentMessageUuid === message.parentMessageUuid);

export const getLatestAiChatLeafUuid = (messages: AiChatMessage[], rootUuid: string): string => {
  // Messages arrive in creation order and children are always created after their
  // parent, so one pass collects the subtree and its most recent leaf.
  const subtreeUuids = new Set([rootUuid]);
  const parentUuidsInSubtree = new Set<string>();
  for (const message of messages) {
    if (message.parentMessageUuid !== null && subtreeUuids.has(message.parentMessageUuid)) {
      subtreeUuids.add(message.uuid);
      parentUuidsInSubtree.add(message.parentMessageUuid);
    }
  }

  let latestLeafUuid = rootUuid;
  for (const uuid of subtreeUuids) {
    if (!parentUuidsInSubtree.has(uuid)) {
      latestLeafUuid = uuid;
    }
  }

  return latestLeafUuid;
};

export const cancelAiChatWorkflow = async (threadId: number): Promise<void> => {
  await apiPost(`ai_chat_threads/${threadId}/cancel`);
};

export const getAiAgentByUser = (user: BackofficeUser) => {
  switch (true) {
    case userHasOneOfTheseRoles(user, [
      Roles.admin,
      Roles.traineeLawyer,
      Roles.lawyer,
      Roles.leadLawyer,
      Roles.developer,
      Roles.productManager,
      Roles.banking,
    ]):
      return "LEXA";
    case userHasOneOfTheseRoles(user, [
      Roles.accountmanager,
      Roles.leadAccountmanager,
      Roles.basicAccounting,
      Roles.accounting,
    ]):
      return "LIA";
    default:
      return null;
  }
};
